import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '@/api/client';
import { ArrowLeft, Check, X, Clock, ExternalLink, Loader2, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { normalizeCoffeeConstrainedFields } from '@/lib/coffeeValidation';

function StatusBadge({ status }) {
  const styles = {
    pending: 'bg-amber-100 text-amber-700',
    approved: 'bg-emerald-100 text-emerald-700',
    rejected: 'bg-rose-100 text-rose-700',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${styles[status]}`}>
      {status === 'pending' && <Clock size={10} />}
      {status === 'approved' && <Check size={10} />}
      {status === 'rejected' && <X size={10} />}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

// A failed write used to be a console.error and nothing else: the spinner
// stopped, the row stayed pending, and the dashboard looked like the button
// had done nothing.
function describeFailure(action, err) {
  const detail = err?.message ? ` ${err.message}` : '';
  return `Could not ${action} this request. Please try again.${detail}`;
}

export default function Admin() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [requests, setRequests] = useState([]);
  const [reviews, setReviews] = useState({});
  const [coffees, setCoffees] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [processingId, setProcessingId] = useState(null);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    api.auth.me().then(async (u) => {
      if (!u || u.role !== 'admin') {
        navigate('/');
        return;
      }
      setUser(u);
      const reqs = await api.entities.CoffeeChangeRequest.list('-created_date', 50);

      // Only the targets the listed requests actually point at, rather than
      // every review and coffee in the database.
      const idsFor = (type) => [
        ...new Set(reqs.filter((r) => r.target_type === type).map((r) => r.target_id)),
      ];
      const reviewIds = idsFor('review');
      const coffeeIds = idsFor('coffee');

      const [relatedReviews, relatedCoffees] = await Promise.all([
        reviewIds.length ? api.entities.Review.filter({ id: reviewIds }) : [],
        coffeeIds.length ? api.entities.Coffee.filter({ id: coffeeIds }) : [],
      ]);
      const revMap = {};
      relatedReviews.forEach((r) => (revMap[r.id] = r));
      const coffeeMap = {};
      relatedCoffees.forEach((c) => (coffeeMap[c.id] = c));
      setReviews(revMap);
      setCoffees(coffeeMap);
      setRequests(reqs);
      setLoading(false);
    }).catch(() => navigate('/login'));
  }, []);

  const handleApprove = async (req) => {
    setProcessingId(req.id);
    setActionError('');
    try {
      if (req.target_type === 'review') {
        if (req.action === 'edit' && req.proposed_changes) {
          await api.entities.Review.update(req.target_id, req.proposed_changes);
        } else if (req.action === 'delete') {
          await api.entities.Review.delete(req.target_id);
        }
      } else if (req.target_type === 'coffee') {
        if (req.action === 'edit' && req.proposed_changes) {
          // A request made against a coffee with no brew method or processing
          // method carries '' for those columns, and '' fails their CHECK
          // constraint. Drop the empty ones so the rest of the edit applies.
          await api.entities.Coffee.update(
            req.target_id,
            normalizeCoffeeConstrainedFields(req.proposed_changes),
          );
        } else if (req.action === 'delete') {
          // ON DELETE CASCADE removes the coffee's reviews and their likes.
          // This used to be a client-side loop: one query per review plus one
          // delete per like, none of it atomic — a failure part-way through
          // left the coffee gone and its reviews orphaned.
          await api.entities.Coffee.delete(req.target_id);
        }
      }
      await api.entities.CoffeeChangeRequest.update(req.id, {
        status: 'approved',
        reviewed_by: user.id,
      });
      setRequests((prev) =>
        prev.map((r) => (r.id === req.id ? { ...r, status: 'approved', reviewed_by: user.id } : r))
      );
    } catch (err) {
      console.error(err);
      setActionError(describeFailure('approve', err));
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (req) => {
    setProcessingId(req.id);
    setActionError('');
    try {
      await api.entities.CoffeeChangeRequest.update(req.id, {
        status: 'rejected',
        reviewed_by: user.id,
      });
      setRequests((prev) =>
        prev.map((r) => (r.id === req.id ? { ...r, status: 'rejected', reviewed_by: user.id } : r))
      );
    } catch (err) {
      console.error(err);
      setActionError(describeFailure('reject', err));
    } finally {
      setProcessingId(null);
    }
  };

  const filteredRequests = requests.filter((r) => r.status === activeTab);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-8">
      <div className="px-4 pt-4 max-w-3xl mx-auto">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft size={16} />
          Back
        </button>

        <h1 className="font-heading text-2xl font-bold mb-1">Admin Dashboard</h1>
        <p className="text-sm text-muted-foreground mb-6">Review and manage change requests</p>

        {/* Action error */}
        {actionError && (
          <div className="mb-4 flex items-start gap-2 rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-xs text-destructive">
            <AlertCircle size={14} className="shrink-0 mt-px" />
            <span>{actionError}</span>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-muted rounded-xl p-1 mb-6">
          {['pending', 'approved', 'rejected'].map((tab) => {
            const count = requests.filter((r) => r.status === tab).length;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                {count > 0 && (
                  <span className="ml-1.5 text-[10px] text-muted-foreground">({count})</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Requests list */}
        {filteredRequests.length > 0 ? (
          <div className="space-y-3">
            {filteredRequests.map((req) => {
              const targetReview = req.target_type === 'review' ? reviews[req.target_id] : null;
              const targetCoffee = req.target_type === 'coffee' ? coffees[req.target_id] : (targetReview ? coffees[targetReview.coffee_id] : null);
              const isProcessing = processingId === req.id;

              return (
                <div key={req.id} className="bg-card rounded-2xl border border-border p-4 space-y-3">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-semibold text-foreground">
                          {req.target_type === 'review' ? 'Review' : 'Coffee'} — {req.action === 'edit' ? 'Edit' : 'Delete'}
                        </span>
                        <StatusBadge status={req.status} />
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        Requested by {req.requester_name || 'Unknown'} · {req.created_date ? format(new Date(req.created_date), 'MMM d, yyyy HH:mm') : ''}
                      </p>
                    </div>
                    {targetCoffee && (
                      <Link
                        to={`/coffee/${targetCoffee.id}`}
                        className="text-[10px] text-primary hover:underline flex items-center gap-1 shrink-0"
                      >
                        <ExternalLink size={10} />
                        View coffee
                      </Link>
                    )}
                  </div>

                  {/* Target info */}
                  {targetCoffee && (
                    <div className="bg-muted rounded-xl px-3 py-2 flex items-center gap-2 text-xs">
                      <span className="font-semibold text-foreground">{targetCoffee.name}</span>
                      <span className="text-muted-foreground">·</span>
                      <span className="text-muted-foreground">{targetCoffee.cafe_name}</span>
                    </div>
                  )}

                  {/* Current review data (for review edit requests) */}
                  {targetReview && req.action === 'edit' && (
                    <div className="text-xs text-muted-foreground space-y-1">
                      <p>Current: Rating {targetReview.rating}/5</p>
                      {targetReview.comment && <p className="line-clamp-2">"{targetReview.comment}"</p>}
                    </div>
                  )}

                  {/* Proposed changes */}
                  {req.proposed_changes && Object.keys(req.proposed_changes).length > 0 && (
                    <div className="bg-primary/5 rounded-xl px-3 py-2 space-y-1">
                      <p className="text-[10px] font-semibold text-primary uppercase tracking-wider">Proposed Changes</p>
                      {Object.entries(req.proposed_changes).map(([key, value]) => (
                        <p key={key} className="text-xs">
                          <span className="text-muted-foreground">{key}:</span>{' '}
                          <span className="font-medium text-foreground">
                            {typeof value === 'string' && value.length > 50 ? `"${value.slice(0, 50)}..."` : String(value)}
                          </span>
                        </p>
                      ))}
                    </div>
                  )}

                  {/* Reason */}
                  {req.reason && (
                    <p className="text-xs text-muted-foreground italic">"{req.reason}"</p>
                  )}

                  {/* Actions */}
                  {req.status === 'pending' && (
                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={() => handleApprove(req)}
                        disabled={isProcessing}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                      >
                        {isProcessing ? <Loader2 size={12} className="animate-spin" /> : <Check size={14} />}
                        Approve
                      </button>
                      <button
                        onClick={() => handleReject(req)}
                        disabled={isProcessing}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-destructive/10 text-destructive text-xs font-semibold hover:bg-destructive/20 disabled:opacity-50 transition-colors"
                      >
                        <X size={14} />
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16 border border-dashed border-border rounded-2xl">
            <Clock size={36} className="mx-auto mb-3 text-muted-foreground opacity-40" strokeWidth={1} />
            <p className="text-sm text-muted-foreground">No {activeTab} requests.</p>
          </div>
        )}
      </div>
    </div>
  );
}